import { updateDataNode } from '@/endpoints'
import { StructureDetailDto } from '@/endpoints/models'
import { ViewData, ViewPermission } from '@/endpoints/schemas'
import { StructureDto } from '@/rtkApi/mddApiSliceGenerated'
import { apiCallAction, AppDispatch, UpdateDeepPartial } from '@/store/utils'
import { assignCollectionOrder } from '@/utils/collectionOrder'
import { InitDataParams } from '@/utils/structureType/useStructureTypeActions'

import { loadNodeOrHistoryVersion } from '../node/utils'
import { inflateTablePermission } from '../table/helpers'
import { VIEW_INIT, VIEW_SAVE, VIEW_SELECT_TAB, VIEW_UPDATE } from './constants'
import { ViewDataForm, ViewTab } from './types'

interface InitView {
	editMode: boolean
	force: boolean
	node: StructureDetailDto
	refreshWorkingCopy?: boolean
	type: typeof VIEW_INIT
}

interface SaveView {
	metadata: {
		node: StructureDto
	}
	payload: void
	type: typeof VIEW_SAVE
}

interface UpdateView {
	node: StructureDto
	type: typeof VIEW_UPDATE
	update: UpdateDeepPartial<ViewDataForm>
}

interface SelectViewTab {
	node: StructureDto
	tab: ViewTab
	type: typeof VIEW_SELECT_TAB
}

export const initView =
	({
		nodeId,
		editMode = false,
		force = false,
		refreshWorkingCopy = false,
		version,
		envId,
	}: InitDataParams) =>
	async (dispatch: AppDispatch) => {
		const node = await loadNodeOrHistoryVersion(nodeId, version, envId)

		dispatch({
			type: VIEW_INIT,
			node,
			editMode,
			force,
			refreshWorkingCopy,
		} as InitView)
	}

export const updateView = (
	node: StructureDto,
	update: UpdateDeepPartial<ViewDataForm>,
): Actions => ({
	type: VIEW_UPDATE,
	node,
	update,
})

export const saveView = (node: StructureDto) =>
	apiCallAction(
		(getState) => {
			const view = getState().view.views[node.id]

			if (!view) {
				throw new Error(`Saving unopened state ${JSON.stringify(node)}`)
			}

			const formData: ViewData = {
				...view.form,
				columns: assignCollectionOrder(
					(view.form.columns || []).filter((x) => x.name),
				),
				sourceItems: assignCollectionOrder(view.form.sourceItems || []),
				objectPermissions: assignCollectionOrder(
					view.form.objectPermissions
						.filter((x) => x.userName)
						.map((x) => inflateTablePermission(x)) as ViewPermission[],
				),
			}

			return updateDataNode(node.id, {
				data: JSON.stringify(formData),
			})
		},
		VIEW_SAVE,
		{ node },
	)

export const selectViewTab = (node: StructureDto, tab: ViewTab): Actions => ({
	type: VIEW_SELECT_TAB,
	node,
	tab,
})

export type Actions = InitView | SaveView | UpdateView | SelectViewTab
